import { BallFinalMetricsConfigurator, BfMetricTileSize, IBfMetricListItem, UnitSystem } from '@trackman/baseball-shared-components';
import EndGameModal from 'components/header/NavBar/GameControl/components/EndGameModal/EndGameModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionMode } from 'enums/SessionMode';
import { History } from 'history';
import { IStrikeZoneDecisionModel } from 'interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { observer } from 'mobx-react-lite';
import { Metrics } from 'pages/Tagging/components/Metrics/Metrics';
import React, { useEffect, useState } from 'react';
import { BroadcastConsumerService } from 'services/data-services/BroadcastConsumerService';
import { useInjection } from 'services/ioc/ioc.react';
import { MetricStore } from 'stores/MetricStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { BpTaggingStore } from 'stores/Tagging/BpTaggingStore';
import { HeightWrapper } from './BpTaggingStyles';
import BpLineupList from './components/LineupList/BpLineupList';
import BpPlayList from './components/PlayList/BpPlayList';
import { BpTaggingFlow } from './components/TaggingFlow/BpTaggingFlow';

interface IProps {
  history: History;
}

const BpTagging: React.FC<IProps> = observer((props) => {
  const { history } = props;
  const taggingStore = useInjection<BpTaggingStore>(ServiceTypes.BpTaggingStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const broadcastService = useInjection<BroadcastConsumerService>(ServiceTypes.BroadcastConsumerService);
  const metricStore = useInjection<MetricStore>(ServiceTypes.MetricStore);
  const [isSubscribedToSessionModeChanges, setIsSubscribedToSessionModeChanges] = useState(true);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  useEffect(() => {
    taggingStore.setIsTagging(true);
    return () => {
      taggingStore.setIsTagging(false);
    };
  }, []);

  const initializeStores = async () => {
    if (!taggingStore.isInitialized) {
      await taggingStore.resumeSession();
    }
  };
  useEffect(() => {
    initializeStores();
  }, []);

  useEffect(() => {
    broadcastService.subscribeBallFinal(onBallFinal);
    broadcastService.subscribeStrikeZoneDecision(onStrikeZoneDecision);
    return () => {
      broadcastService.unsubscribeBallEvents();
    };
  }, []);

  const onStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecisionModel) => {
    taggingStore.handleStrikeZoneDecision(strikeZoneDecision);
  };

  useEffect(() => {
    if (isSubscribedToSessionModeChanges && sessionStore.currentSessionMode.sessionMode === SessionMode.SessionEnded) {
      notificationStore.addNotification({
        id: 'session-mode-error',
        message: 'Session was ended while you were tagging',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('session-mode-error');
        },
      });
      history.push('/');
    }
  }, [sessionStore.currentSessionMode]);

  const onBallFinal = (ballFinal: any) => {
    taggingStore.createOrUpdatePlayFromBroadcast(ballFinal.ballFinal);
  };

  const handleOnEndGameCallback = async () => {
    setIsSubscribedToSessionModeChanges(false);
    const isSuccess = await sessionStore.setCurrentSessionMode({
      ...sessionStore.currentSessionMode,
      sessionMode: SessionMode.SessionEnded,
      issuer: 'TaggingApp',
    });
    sessionStore.setIsEndGameModalVisible(false);
    if (isSuccess) {
      history.push(`/review-bp/${sessionStore.currentSessionMode.sessionId}`);
    } else {
      notificationStore.addNotification({
        id: 'session-mode-error',
        message: 'Error changing session mode',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('session-mode-error');
        },
      });
    }
  };

  return taggingStore.isInitialized ? (
    <>
      <div>
        <div className='row'>
          <div className='col-2'>
            <HeightWrapper>
              <BpPlayList />
            </HeightWrapper>
          </div>
          <div className='col-3'>
            <HeightWrapper>
              <BpLineupList disabled={!taggingStore.isLivePlaySelected} />
            </HeightWrapper>
          </div>
          <div className='col-5'>
            <HeightWrapper>
              <BpTaggingFlow selectedPlay={taggingStore.selectedPlay} disabled={!taggingStore.isLivePlaySelected} />
            </HeightWrapper>
          </div>
          <div className='col-2'>
            <HeightWrapper>
              <Metrics
                ballFinals={taggingStore.selectedPlay?.ballFinals}
                connected={sessionStore.isDataBroadcasterConnected}
                strikeZoneDecision={taggingStore.selectedPlay?.strikeZoneDecision}
              />
            </HeightWrapper>
          </div>
          <BallFinalMetricsConfigurator
            metrics={metricStore.filteredMetrics}
            tileSize={metricStore.tileSize}
            unitSystem={metricStore.unitSystem}
            onConfigChanged={(metrics: IBfMetricListItem[], unitSystem: UnitSystem, tileSize: BfMetricTileSize) => {
              metricStore.setMetrics(metrics);
              metricStore.setTileSize(tileSize);
              metricStore.setUnitSystem(unitSystem);
            }}
            onHide={() => metricStore.setIsSettingsMenuVisible(false)}
            isVisible={metricStore.isSettingsMenuVisible}
            width='300px'
          />
        </div>
      </div>
      {sessionStore.isEndGameModalVisible && (
        <EndGameModal dismissCallback={() => sessionStore.setIsEndGameModalVisible(false)} endGameCallback={handleOnEndGameCallback} />
      )}
    </>
  ) : null;
});

export default BpTagging;
